import { useState } from 'react';

const FAQS = [
  {
    q: 'Are you a qualified electrician in Sleaford?',
    a: 'Yes — Quantum Electrical Services are NAPIT registered, Part P certified electricians based in Sleaford, Lincolnshire. Every job is carried out to the latest 18th Edition IET Wiring Regulations and fully certificated on completion.',
  },
  {
    q: 'What areas around Sleaford do you cover?',
    a: 'We cover Sleaford and the surrounding Lincolnshire area including Grantham, Lincoln, Boston, Bourne, Spalding, Stamford, Newark, and many of the villages in between. If you\'re unsure whether we reach you, just give us a call on 01529 265060.',
  },
  {
    q: 'Do you provide free quotes?',
    a: 'Absolutely. We offer free, no-obligation quotes on all electrical work. We\'ll assess the job, give you a clear written price, and only start work once you\'re happy to proceed — no hidden charges or surprises.',
  },
  {
    q: 'Can you carry out a full house rewire in Sleaford?',
    a: 'Yes. Full and partial rewires are one of our most common jobs. We work efficiently to minimise disruption, and every rewire is tested and certificated to Part P standards once complete.',
  },
  {
    q: 'Do you install EV car chargers in Sleaford?',
    a: 'Yes — we install home EV charging points for all major vehicle makes. As an OZEV authorised installer we can handle the full process from survey and installation through to any grant paperwork.',
  },
  {
    q: 'What is an EICR and do I need one?',
    a: 'An Electrical Installation Condition Report (EICR) is a formal inspection of your property\'s wiring and consumer unit. Landlords are legally required to have one every 5 years. Homeowners are advised to get one when buying a property or if the electrics haven\'t been checked in a long time. We carry out EICR inspections across Sleaford and Lincolnshire.',
  },
  {
    q: 'Can you upgrade my fuse board / consumer unit?',
    a: 'Yes. Upgrading an old fuse board to a modern consumer unit with RCD and RCBO protection is one of the most important safety improvements you can make. We carry out fuse board upgrades throughout Sleaford and certify them under Part P.',
  },
  {
    q: 'How quickly can you respond to an electrical emergency in Sleaford?',
    a: 'We aim to respond to emergency call-outs as quickly as possible. Call us on 01529 265060 and we\'ll do our best to attend the same day. For non-urgent work we\'ll arrange a convenient appointment around you.',
  },
  {
    q: 'Are you insured?',
    a: 'Yes — Quantum Electrical Services holds full public liability insurance and employer\'s liability insurance. Copies of our insurance and registration certificates are available on request.',
  },
  {
    q: 'What commercial electrical work do you do in Sleaford?',
    a: 'We handle a wide range of commercial electrical work including new installations, rewires, lighting upgrades, three-phase power, distribution boards, and EICR inspections for businesses across Sleaford and Lincolnshire.',
  },
];

function FAQItem({ q, a, open, onToggle }: { q: string; a: string; open: boolean; onToggle: () => void }) {
  return (
    <div
      className="border border-[#C8A96A]/15 rounded-xl overflow-hidden transition-all duration-300"
      style={{ background: open ? 'linear-gradient(135deg, #0D1E35, #0A1628)' : 'rgba(10,22,40,0.6)' }}
      itemScope
      itemProp="mainEntity"
      itemType="https://schema.org/Question"
    >
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left cursor-pointer group"
        aria-expanded={open}
      >
        <span
          className="font-semibold text-sm md:text-base leading-snug transition-colors duration-200"
          style={{ color: open ? '#E0C78A' : 'rgba(255,255,255,0.85)' }}
          itemProp="name"
        >
          {q}
        </span>
        <div
          className="w-7 h-7 flex-shrink-0 flex items-center justify-center rounded-full border border-[#C8A96A]/30 transition-all duration-300"
          style={{ background: open ? 'rgba(200,169,106,0.15)' : 'transparent' }}
        >
          <i
            className="ri-add-line text-[#C8A96A] text-base transition-transform duration-300"
            style={{ transform: open ? 'rotate(45deg)' : 'rotate(0deg)' }}
          />
        </div>
      </button>

      <div
        className="overflow-hidden transition-all duration-400 ease-in-out"
        style={{ maxHeight: open ? '300px' : '0px' }}
        itemScope
        itemProp="acceptedAnswer"
        itemType="https://schema.org/Answer"
      >
        <p
          className="px-6 pb-6 text-sm text-white/60 leading-relaxed"
          itemProp="text"
        >
          {a}
        </p>
      </div>
    </div>
  );
}

export default function FAQ() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const toggle = (i: number) => setOpenIndex(openIndex === i ? null : i);

  return (
    <section
      id="faq"
      className="py-20 md:py-28 bg-[#0A1628] relative overflow-hidden"
      itemScope
      itemType="https://schema.org/FAQPage"
    >
      {/* Background glow */}
      <div
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[400px] rounded-full pointer-events-none"
        style={{ background: 'radial-gradient(ellipse, rgba(200,169,106,0.04) 0%, transparent 70%)' }}
      />

      <div className="max-w-4xl mx-auto px-4 md:px-6 relative z-10">
        {/* Header */}
        <div className="text-center mb-14">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 border border-[#C8A96A]/25 rounded-full mb-5">
            <i className="ri-question-answer-line text-[#C8A96A] text-xs" />
            <span className="text-[#C8A96A] text-xs tracking-widest uppercase font-medium">FAQs</span>
          </div>
          <h2 className="font-playfair text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
            Frequently Asked{' '}
            <span className="bg-gradient-to-r from-[#C8A96A] to-[#E0C78A] bg-clip-text text-transparent">
              Questions
            </span>
          </h2>
          <p className="text-white/50 text-sm md:text-base max-w-xl mx-auto">
            Common questions about our <strong className="text-white/70">electrician services in Sleaford</strong> and across Lincolnshire.
          </p>
        </div>

        {/* FAQ list */}
        <div className="flex flex-col gap-3">
          {FAQS.map((item, i) => (
            <FAQItem
              key={i}
              q={item.q}
              a={item.a}
              open={openIndex === i}
              onToggle={() => toggle(i)}
            />
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="mt-12 text-center">
          <p className="text-white/40 text-sm mb-4">Still have a question? We\'re happy to help.</p>
          <a
            href="tel:01529265060"
            className="inline-flex items-center gap-2 bg-gradient-to-r from-[#C8A96A] to-[#A8894F] hover:from-[#E0C78A] hover:to-[#C8A96A] text-black font-bold px-8 py-3.5 rounded-full transition-all duration-300 cursor-pointer whitespace-nowrap text-sm"
          >
            <i className="ri-phone-fill" />
            Call 01529 265060
          </a>
        </div>
      </div>
    </section>
  );
}
